/* eslint-disable @typescript-eslint/no-explicit-any */
import Cookies from 'js-cookie';

import { EasyshipLocale, SUPPORTED_LOCALES } from '@/providers/I18nProvider/types';

const LOCALE_COOKIE_NAME = 'easyship-insure-locale';

const localeModules = import.meta.glob<{ default: Record<string, any> }>('../locales/*.json');

export const flattenMessages = (data: Record<string, any>): Record<string, string> => {
  const result = {} as Record<string, any>;

  function recurse(cur: Record<string, any>, prop: string) {
    let i;
    let l;
    let p;
    let isEmpty;
    if (Object(cur) !== cur) {
      result[prop] = cur;
    } else if (Array.isArray(cur)) {
      for (i = 0, l = cur.length; i < l; i++) recurse(cur[i], `${prop}[${i}]`);
      if (l === 0) result[prop] = [];
    } else {
      isEmpty = true;
      for (p in cur) {
        isEmpty = false;
        recurse(cur[p], prop ? `${prop}.${p}` : p);
      }
      if (isEmpty && prop) result[prop] = {};
    }
  }

  recurse(data, '');
  return result;
};

export const getLocaleFromUrl = (defaultLocale: EasyshipLocale = 'en'): EasyshipLocale => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const localeParam = urlParams.get('locale');

    if (localeParam && SUPPORTED_LOCALES.includes(localeParam as EasyshipLocale)) {
      return localeParam as EasyshipLocale;
    }

    const cookieLocale = Cookies.get(LOCALE_COOKIE_NAME);
    if (cookieLocale && SUPPORTED_LOCALES.includes(cookieLocale as EasyshipLocale)) {
      return cookieLocale as EasyshipLocale;
    }

    return defaultLocale;
  } catch (_error) {
    return defaultLocale;
  }
};

export const isValidLocale = (locale: string): locale is EasyshipLocale => {
  return SUPPORTED_LOCALES.includes(locale as EasyshipLocale);
};

export const saveLocaleToStorage = (locale: EasyshipLocale): void => {
  try {
    Cookies.set(LOCALE_COOKIE_NAME, locale, {
      sameSite: 'none',
      secure: true,
      expires: 30 / (24 * 60),
    });
  } catch (_error) {
    //
  }
};

const loadLocaleFile = async (locale: EasyshipLocale): Promise<Record<string, string> | null> => {
  const loader = localeModules[`../locales/${locale}.json`];
  if (!loader) {
    return null;
  }

  const rawMessages = await loader();
  return flattenMessages({ ...rawMessages.default });
};

export const loadLocaleMessages = async (
  locale: EasyshipLocale,
): Promise<Record<string, string> | null> => {
  try {
    const enMessages = await loadLocaleFile('en');
    if (!enMessages) {
      return null;
    }

    if (locale === 'en') {
      return enMessages;
    }

    const localeMessages = await loadLocaleFile(locale);
    if (!localeMessages) {
      return enMessages;
    }

    // Prefer translated strings; fall back to English for missing keys
    const translatedMessages = Object.fromEntries(
      Object.entries(localeMessages).filter(
        ([, value]) => typeof value === 'string' && value !== '',
      ),
    );

    return { ...enMessages, ...translatedMessages };
  } catch (_error) {
    return null;
  }
};
